// src/components/AboutUs/LeadershipProfileCard.tsx

import React from 'react';
import { motion, Variants } from 'framer-motion';
import { User, Award, Linkedin, ChevronRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import styles from '../../css/AboutUsPage.module.css';

interface Props {
    name: string;
    title: string;
    expertiseSummary: string;
    image: string | null;
    linkedinUrl?: string;
    slug?: string; // New prop for profile navigation
    variants?: Variants;
}

const LeadershipProfileCard: React.FC<Props> = ({ 
    name, title, expertiseSummary, image, linkedinUrl, slug, variants 
}) => {
    const navigate = useNavigate();

    const handleCardClick = () => {
        if (slug) {
            navigate(slug);
            window.scrollTo(0, 0);
        }
    };

    const handleLinkedinClick = (e: React.MouseEvent) => {
        e.stopPropagation(); // Prevent triggering the card click
    };

    return (
        <motion.div 
            className={`${styles.profileCard} ${slug ? styles.clickableCard : ''}`}
            variants={variants}
            onClick={handleCardClick}
            whileHover={slug ? { y: -5, borderColor: "var(--zinc-400)" } : {}}
        >
            <div className={styles.cardTopRow}>
                <div className={styles.profileHeader}>
                    <div className={styles.profileImageWrapper}>
                        {image ? (
                            <img src={image} alt={name} className={styles.profileImage} />
                        ) : (
                            <div className={styles.profilePlaceholder}>
                                <User size={24} strokeWidth={1.5} />
                            </div>
                        )}
                    </div>
                    <div className={styles.headerText}>
                        <h3 className={styles.profileName}>{name}</h3>
                        <p className={styles.profileTitle}>{title}</p>
                    </div>
                </div>
                
                {linkedinUrl && (
                    <a 
                        href={linkedinUrl} 
                        target="_blank" 
                        rel="noopener noreferrer" 
                        className={styles.linkedinBtn}
                        onClick={handleLinkedinClick}
                        aria-label={`View ${name}'s LinkedIn Profile`}
                    >
                        <Linkedin size={18} strokeWidth={1.5} />
                    </a>
                )}
            </div>
            
            <div className={styles.profileDivider}></div>
            
            <div className={styles.profileBody}>
                <Award size={18} className={styles.expertiseIcon} />
                <p className={styles.profileExpertise}>{expertiseSummary}</p>
            </div>

            {/* Visual cue for clickable cards */}
            {slug && (
                <div className={styles.viewProfileCue}>
                    <span>View Full Profile</span> <ChevronRight size={14} />
                </div>
            )}
        </motion.div>
    );
};

export default LeadershipProfileCard;